import React, { useMemo } from "react";
import { Modal, ModalBody, Card, CardBody, Badge, Progress } from "reactstrap";

/**
 * ClassPlanModal
 * Modal compacto de Planejamento de Aula.
 */
const ClassPlanModal = ({ isOpen, onClose, session }) => {
    const plan = session?.lessonPlan;

    const objectivesList = useMemo(() => {
        if (!plan?.objectives) return [];
        return plan.objectives;
    }, [plan]);

    if (!session) return null;

    const activityName = session.activityName || "Atividade";
    const sessionDate = session.sessionDate || "";
    const startTime = session.startTime || "";
    const endTime = session.endTime || "";
    const employeeName = session.employeeName || "";

    const dateFormatted = sessionDate
        ? new Date(sessionDate + "T12:00:00").toLocaleDateString("pt-BR", {
            weekday: "long",
            day: "2-digit",
            month: "long"
        })
        : "";

    const getScoreColor = (avgScore, maxLevel = 4) => {
        const pct = maxLevel > 0 ? (avgScore / maxLevel) * 100 : 0;
        if (pct >= 75) return "success";
        if (pct >= 50) return "info";
        if (pct >= 25) return "warning";
        return "danger";
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" centered scrollable>
            {/* Header compacto */}
            <div className="bg-white py-3 px-4 border-bottom">
                <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                        <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm font-size-20">
                            <i className="mdi mdi-clipboard-text-outline"></i>
                        </span>
                    </div>
                    <div className="ms-3 overflow-hidden flex-grow-1">
                        <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                            <span className="text-truncate">{activityName}</span>
                            {plan && (
                                <span className="badge bg-soft-primary text-primary pill font-size-10 px-2 flex-shrink-0">
                                    Ciclo {plan.cyclePosition}/{plan.cycleLength}
                                </span>
                            )}
                        </h5>
                        <p className="text-muted mb-0 small text-truncate text-capitalize">
                            {dateFormatted} • {startTime}–{endTime}
                            {employeeName && <> • {employeeName}</>}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn-close flex-shrink-0"
                        onClick={onClose}
                        aria-label="Fechar"
                    />
                </div>
            </div>

            <ModalBody className="p-3 bg-light-subtle">
                {/* Info inline */}
                {plan && (
                    <div className="d-flex align-items-center gap-4 mb-3 px-1">
                        <span className="font-size-12 text-muted">
                            <i className="mdi mdi-account-group me-1 text-primary"></i>
                            <strong>{plan.classMemberCount || 0}</strong> alunos
                        </span>
                        <span className="font-size-12 text-muted">
                            <i className="mdi mdi-chart-line me-1 text-primary"></i>
                            Média: <strong className={`text-${getScoreColor(plan.classAvgOverall)}`}>{plan.classAvgOverall?.toFixed(1) || "0.0"}</strong>
                        </span>
                        <span className="font-size-12 text-muted">
                            <i className="mdi mdi-target me-1 text-primary"></i>
                            <strong>{objectivesList.length}</strong> objetivo(s)
                        </span>
                    </div>
                )}

                {/* Sem plano */}
                {!plan && (
                    <div className="text-center py-5">
                        <div className="avatar-lg mx-auto mb-3">
                            <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                                <i className="mdi mdi-clipboard-text-outline"></i>
                            </div>
                        </div>
                        <h5>Nenhum planejamento gerado</h5>
                        <p className="text-muted font-size-13 mb-0">
                            Clique em <strong>"Gerar Planejamento"</strong> para criar o plano desta turma.
                        </p>
                    </div>
                )}

                {/* Objetivos */}
                {plan && objectivesList.length > 0 && (
                    <div className="d-flex flex-column gap-2">
                        {objectivesList.map((objective, idx) => (
                            <Card key={objective.id || idx} className="border shadow-none rounded-0 mb-0">
                                <CardBody className="p-3">
                                    {/* Header do Objetivo */}
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="mdi mdi-bullseye-arrow text-primary font-size-16"></i>
                                            <span className="fw-bold font-size-14">{objective.name}</span>
                                            <Badge
                                                color={
                                                    objective.reason === "fundamental" ? "warning"
                                                        : objective.reason === "priority" ? "danger"
                                                            : "secondary"
                                                }
                                                className="font-size-9"
                                                pill
                                            >
                                                {objective.reason === "fundamental" ? "Fundamental"
                                                    : objective.reason === "priority" ? "Prioridade"
                                                        : "Regular"}
                                            </Badge>
                                        </div>
                                        <span className={`fw-bold font-size-16 text-${getScoreColor(objective.avgScore)}`}>
                                            {objective.avgScore?.toFixed(1) || "0.0"}
                                        </span>
                                    </div>

                                    <Progress
                                        value={objective.avgScore || 0}
                                        max={4}
                                        color={getScoreColor(objective.avgScore)}
                                        className="mb-2"
                                        style={{ height: "4px" }}
                                    />

                                    {/* Tópicos compactos */}
                                    {objective.topics && objective.topics.length > 0 && (
                                        <div className="d-flex flex-column gap-1">
                                            {objective.topics.map((topic, tIdx) => (
                                                <div
                                                    key={topic.id || tIdx}
                                                    className="d-flex align-items-center justify-content-between py-1 px-2 rounded bg-light"
                                                    style={{ fontSize: "12px" }}
                                                >
                                                    <div className="d-flex align-items-center gap-1">
                                                        {topic.isFundamental ? (
                                                            <i className="mdi mdi-star text-warning font-size-12"></i>
                                                        ) : (
                                                            <i className="mdi mdi-circle-small text-muted font-size-14"></i>
                                                        )}
                                                        <span>{topic.name}</span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2" style={{ minWidth: "100px" }}>
                                                        <Progress
                                                            value={topic.avg || 0}
                                                            max={4}
                                                            color={getScoreColor(topic.avg)}
                                                            className="flex-grow-1"
                                                            style={{ height: "3px" }}
                                                        />
                                                        <span className={`fw-bold text-${getScoreColor(topic.avg)}`} style={{ minWidth: "24px", textAlign: "right", fontSize: "11px" }}>
                                                            {topic.avg?.toFixed(1) || "0.0"}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

export default ClassPlanModal;
